import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router';

import { updateHolderRequest } from '@/api/operation';

import { LoadingButton } from '@/components/Form/LoadingButton';
import { Icon } from '@/components/Icon/Icon';

import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectUserHolderJointMinor } from '@/redux/user-data/selectors';

import { getDataTestId } from '@/utils/browser';

export function MinorReviewCompleteForm() {
  const { t, language } = useTranslate();
  const navigate = useNavigate();
  const user = useAppSelector(selectLoggedInUser);
  const minorHolder = useAppSelector(selectUserHolderJointMinor);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (user?.operationId && minorHolder) {
      setIsSubmitting(true);
      try {
        await updateHolderRequest(user.operationId, minorHolder.clientId, {
          firstName: minorHolder.firstName,
          middleName: minorHolder.middleName ?? undefined,
          lastName: minorHolder.lastName,
          email: minorHolder.email,
        });
      } catch (error) {
        console.error('Failed to update minor info:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
    navigate(ROUTE.MORE_INFORMATION, {
      replace: true,
      state: { openMinorTab: true, openIdentityCheckDrawer: true },
    });
  };

  return (
    <Stack gap={4} width="100%" maxWidth={400}>
      <Trans
        lang={language}
        i18nKey="verification.verifyMinorID.reviewAndComplete.title"
        components={{
          a1: (
            <Typography
              variant="h1"
              component="span"
              color={theme => theme.palette.primaries[500]}
            />
          ),
        }}
      />
      {minorHolder && (
        <Stack gap={2}>
          <Typography variant="body2" color="text.secondary">
            {[
              minorHolder.firstName,
              minorHolder.middleName,
              minorHolder.lastName,
            ]
              .filter(Boolean)
              .join(' ')}
          </Typography>
        </Stack>
      )}
      <LoadingButton
        {...getDataTestId('minor-review-confirm')}
        variant="contained"
        size="large"
        fullWidth
        isLoading={isSubmitting}
        onClick={handleConfirm}
      >
        {t('verification.verifyMinorID.reviewAndComplete.confirm')}
      </LoadingButton>
      <Box display="flex" alignItems="center" gap={1}>
        <Icon name="lock" color={theme => theme.palette.grey[600]} />
        <Typography variant="caption" color="text.secondary">
          {t('verification.verifySelfie.caption')}
        </Typography>
      </Box>
    </Stack>
  );
}
